import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { availableLetters } from '../../avatar';
import { drillPool, nextWord, SETTLE_MS, spellMs } from '../lib/practice';
import type { Speed } from '../lib/types';
import {
  initialPracticeState,
  practiceReducer,
  type PracticeState,
} from './practice';

export interface PracticeApi {
  state: PracticeState;
  /** Words the alphabet can honestly spell. Empty means the drill cannot run. */
  poolSize: number;
  start(): void;
  next(): void;
  replay(): void;
  submit(): void;
  setAnswer(answer: string): void;
  setSpeed(speed: Speed): void;
}

/**
 * Owns the drill's clock and word choice; the reducer owns every decision.
 * Mirrors useSignpost: the timer that ends the spelling phase keys off `run`,
 * so a replay re-arms it and a stale timer is ignored by the reducer.
 */
export function usePractice({ active = true }: { active?: boolean } = {}): PracticeApi {
  const pool = useMemo(() => drillPool(new Set(availableLetters())), []);
  const [state, dispatch] = useReducer(practiceReducer, initialPracticeState);

  // Gated on `active` as well as on the phase: the drill lives at App level so
  // a streak survives a trip to the chat, but its clock must not keep running
  // while the stage is unmounted — the word would finish unseen and the
  // learner would come back to an ask for something they never watched.
  useEffect(() => {
    if (!active || state.phase !== 'signing' || !state.word) return;
    const timer = setTimeout(
      () => dispatch({ type: 'shown' }),
      spellMs(state.word, state.speed) + SETTLE_MS,
    );
    return () => clearTimeout(timer);
  }, [active, state.phase, state.run, state.word, state.speed]);

  const start = useCallback(() => {
    const word = nextWord(pool, state.word);
    if (word) dispatch({ type: 'start', word });
  }, [pool, state.word]);

  const next = useCallback(() => {
    const word = nextWord(pool, state.word);
    if (word) dispatch({ type: 'next', word });
  }, [pool, state.word]);

  return {
    state,
    poolSize: pool.length,
    start,
    next,
    replay: useCallback(() => dispatch({ type: 'replay' }), []),
    submit: useCallback(() => dispatch({ type: 'submit' }), []),
    setAnswer: useCallback((answer: string) => dispatch({ type: 'setAnswer', answer }), []),
    setSpeed: useCallback((speed: Speed) => dispatch({ type: 'setSpeed', speed }), []),
  };
}
